import { onBeforeUnmount, onMounted, ref, type Ref } from 'vue'

/** Ancho mínimo al que se puede arrastrar una imagen. */
const MIN_WIDTH = 40

export type ImageResizeCorner = 'nw' | 'ne' | 'sw' | 'se'

/** Esquinas del marco, con su posición y su cursor. */
export const IMAGE_RESIZE_CORNERS: ReadonlyArray<{ name: ImageResizeCorner; class: string }> = [
  { name: 'nw', class: 'left-0 top-0 cursor-nwse-resize' },
  { name: 'ne', class: 'right-0 top-0 cursor-nesw-resize' },
  { name: 'sw', class: 'left-0 bottom-0 cursor-nesw-resize' },
  { name: 'se', class: 'right-0 bottom-0 cursor-nwse-resize' },
]

export interface ImageOverlayBox {
  left: number
  top: number
  width: number
  height: number
}

/**
 * Redimensiona con el mouse las imágenes del cuerpo del correo.
 *
 * El tamaño se escribe como `width` en línea sobre el <img> (estilo Y atributo)
 * porque es lo único que respetan Outlook y Gmail al recibir: una clase del
 * redactor no viaja dentro del correo.
 *
 * Las coordenadas del marco se calculan contra `wrapperRef`, que es el
 * contenedor posicionado donde se dibuja; el editor hace scroll por dentro, así
 * que hay que volver a medir cada vez que se desplaza.
 */
export function useEditorImageResize(
  editorRef: Ref<HTMLElement | null>,
  wrapperRef: Ref<HTMLElement | null>,
  onChange: () => void
) {
  const overlay = ref<ImageOverlayBox | null>(null)
  const isResizing = ref(false)
  const size = ref({ width: 0, height: 0 })

  let selected: HTMLImageElement | null = null
  /** Imágenes ya evaluadas en esta sesión del editor, para no medirlas en cada tecla. */
  const seen = new WeakSet<HTMLImageElement>()

  /** Ancho útil del redactor: el máximo al que puede llegar una imagen. */
  const maxWidth = (): number => {
    const editor = editorRef.value
    if (!editor) return 0
    const style = getComputedStyle(editor)
    return editor.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight)
  }

  const applyWidth = (img: HTMLImageElement, width: number) => {
    const px = Math.round(width)
    img.style.width = `${px}px`
    img.style.height = 'auto'
    img.style.maxWidth = '100%'
    img.setAttribute('width', String(px))
    // El alto viejo deformaría la imagen al cambiarle solo el ancho.
    img.removeAttribute('height')
  }

  /**
   * Encaja al ancho del redactor las imágenes que acaban de entrar (pegadas, de
   * una plantilla o de la cita del original): una captura de pantalla se salía
   * del redactor y se montaba sobre lo que tenía al lado.
   *
   * Una imagen que ya trae tamaño propio se respeta: puede ser el logo de una
   * firma o algo que el usuario ya ajustó a mano.
   */
  const fitNewImages = () => {
    const editor = editorRef.value
    if (!editor) return
    const limit = maxWidth()
    if (limit <= 0) return

    editor.querySelectorAll('img').forEach(img => {
      if (seen.has(img)) return
      seen.add(img)

      const fit = () => {
        img.style.maxWidth = '100%'
        if (img.style.width || img.getAttribute('width')) return
        if (img.naturalWidth > limit) applyWidth(img, limit)
      }

      if (img.complete && img.naturalWidth > 0) fit()
      else img.addEventListener('load', fit, { once: true })
    })
  }

  /** Recalcula el marco a partir de dónde está la imagen ahora mismo. */
  const measure = () => {
    const editor = editorRef.value
    const wrapper = wrapperRef.value

    if (!selected || !editor || !wrapper || !editor.contains(selected)) {
      selected = null
      overlay.value = null
      return
    }

    const image = selected.getBoundingClientRect()
    const view = editor.getBoundingClientRect()

    // Imagen desplazada fuera de la parte visible del editor: se esconde el
    // marco en vez de dibujarlo encima de la barra de formato o los adjuntos.
    if (image.bottom < view.top || image.top > view.bottom) {
      overlay.value = null
      return
    }

    const base = wrapper.getBoundingClientRect()
    overlay.value = {
      left: image.left - base.left,
      top: image.top - base.top,
      width: image.width,
      height: image.height,
    }
    size.value = { width: Math.round(image.width), height: Math.round(image.height) }
  }

  const clearSelection = () => {
    selected = null
    overlay.value = null
  }

  /** Selecciona la imagen sobre la que se hizo clic dentro del editor. */
  const selectImageAt = (event: MouseEvent) => {
    const target = event.target as HTMLElement | null
    if (target?.tagName === 'IMG') {
      selected = target as HTMLImageElement
      measure()
      return
    }
    clearSelection()
  }

  const startResize = (event: MouseEvent, corner: ImageResizeCorner) => {
    const img = selected
    if (!img) return

    event.preventDefault()
    event.stopPropagation()

    const rect = img.getBoundingClientRect()
    const startX = event.clientX
    const startWidth = rect.width
    // Arrastrar desde una esquina izquierda achica al mover hacia la derecha.
    const direction = corner === 'ne' || corner === 'se' ? 1 : -1
    const limit = maxWidth()

    isResizing.value = true

    const onMove = (moveEvent: MouseEvent) => {
      const next = startWidth + (moveEvent.clientX - startX) * direction
      applyWidth(img, Math.min(Math.max(next, MIN_WIDTH), limit))
      // La proporción la mantiene el propio navegador con height:auto, así que
      // el alto que se muestra sale de medir la imagen ya redimensionada.
      measure()
    }

    const onUp = () => {
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup', onUp)
      document.body.classList.remove('select-none')
      isResizing.value = false
      measure()
      onChange()
    }

    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onUp)
    document.body.classList.add('select-none')
  }

  /** Un clic fuera de la imagen o de sus manijas quita la selección. */
  const onDocumentMouseDown = (event: MouseEvent) => {
    const target = event.target as HTMLElement | null
    if (!target) return
    if (target.closest('[data-image-resizer]')) return
    if (target.tagName === 'IMG' && editorRef.value?.contains(target)) return
    clearSelection()
  }

  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Escape') clearSelection()
  }

  onMounted(() => {
    // Firefox trae su propio redimensionado de imágenes en contenteditable y
    // se solaparía con este; Chrome ya no lo tiene.
    try {
      document.execCommand('enableObjectResizing', false, 'false')
    } catch {
      // Navegador sin soporte: no hay nada que apagar.
    }

    editorRef.value?.addEventListener('scroll', measure, { passive: true })
    window.addEventListener('resize', measure)
    document.addEventListener('mousedown', onDocumentMouseDown, true)
    document.addEventListener('keydown', onKeyDown)
  })

  onBeforeUnmount(() => {
    editorRef.value?.removeEventListener('scroll', measure)
    window.removeEventListener('resize', measure)
    document.removeEventListener('mousedown', onDocumentMouseDown, true)
    document.removeEventListener('keydown', onKeyDown)
  })

  return {
    imageOverlay: overlay,
    isResizingImage: isResizing,
    imageSize: size,
    fitNewImages,
    selectImageAt,
    startResize,
    clearImageSelection: clearSelection,
    measureImageOverlay: measure,
  }
}
